import { FastField, FieldArray, Formik } from "formik"
import _ from "lodash"
import { observer } from "mobx-react-lite"
import { View } from "native-base"
import React, { useState } from "react"
import { Appearance, TextStyle, TouchableOpacity, ViewStyle } from "react-native"
import * as yup from "yup"
import { Hatchery, Pond, Species } from "../../../database"
import { translate, TxKeyPath } from "../../../i18n"
import { color } from "../../../theme"
import { DateTimePicker, DateTimePickerProps } from "../../date-time-picker/date-time-picker"
import { FormControl, FormControlProps } from "../../form-control/form-control"
import { NumeralInput, NumeralInputProps } from "../../input/numeral-input/numeral-input"
import { JalaIcon } from "../../jala-icon/jala-icon"
import { ModelPicker, ModelPickerProps } from "../../model-picker/model-picker"
import { Picker, PickerProps } from "../../picker/picker"
import { Text } from "../../text/text"
import { FormProps } from "../form"

const CONTAINER: ViewStyle = {
  paddingBottom: 12
}

const FORM_DROPDOWN: ViewStyle = {
  paddingHorizontal: 16,
  marginBottom: 8
}

const BTN_SEPARATOR: ViewStyle = {
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingHorizontal: 16
}

const FLEX_WRAPPER: ViewStyle = {
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingHorizontal: 16
}

const FLEX_CONTENT: ViewStyle = {
  width: '47%'
}

const TEXT_NEXT: TextStyle = {
  textAlign: 'right'
}

const ACTION_SHEET: ViewStyle = {
  height: 170,
  paddingHorizontal: 16
}

const seedType = [
  { value: 'net', id: 1, name: 'LongNet', short: 'Net', tx: 'LongNet' },
  { value: 'gross', id: 2, name: 'LongGross', short: 'Gross', tx: 'LongGross' },
  { value: 'actual', id: 3, name: 'LongActual', short: 'Actual', tx: 'LongActual' },
];

const trayCount = [
  { id: 1, name: '1' },
  { id: 2, name: '2' },
  { id: 3, name: '3' },
  { id: 4, name: '4' }
];

export interface CreateCycleFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: Array<any>
  next?: boolean
  onChange?: (index: number, key: string, value: any) => void
  onPress?: () => void
}

const NumeralField = (
  { name, index, formControlProps, numeralInputProps, label, flex = false, colorScheme = Appearance.getColorScheme(), onChange }:
    { name: string, index?: number, onChange?: (index, key, value) => void, colorScheme?: "light" | "dark", formControlProps?: FormControlProps, numeralInputProps?: NumeralInputProps, label?: string, flex?: boolean },
  ) => (
    <FastField name={name}>
      {({ field, form: { setFieldValue, handleBlur }, meta }) => (
        <FormControl
          isRequired={label==="seed"}
          isInvalid={meta.touched && !!meta.error}
          labelTx={`cycleForm.${_.camelCase(label)}` as TxKeyPath}
          colorScheme={colorScheme}
          inputProps={{
            testID: `jala:id/${field.name}-input`,
            autoCapitalize: "none",
            autoComplete: "off",
            autoCorrect: false,
            textContentType: "none",
            keyboardType: "numeric",
            // onSubmitEditing: () =>  (isValid && handleSubmit()),
            placeholderTx: `cycleForm.${_.camelCase(label)}Placeholder` as TxKeyPath,
            defaultValue: field.value,
            onBlur: handleBlur(field.name),
          }}
          errorMessage={meta.error}
          style={flex ? FLEX_CONTENT : FORM_DROPDOWN}
          {...formControlProps}
        >
          {(inputProps) => (
            <NumeralInput colorScheme={colorScheme}
              onChangeNumber={(value) => {
                setFieldValue(field.name, value)
                onChange(index, label, value)
              }}
              {...inputProps} {...numeralInputProps} />
          )}
        </FormControl>
      )}
  </FastField>
)

const DateField = (
  { name, formControlProps, dateTimePickerProps, colorScheme, label, index }:
  { name: string, colorScheme?: "light" | "dark", label?: string, index?: number, formControlProps?: FormControlProps, dateTimePickerProps?: Omit<DateTimePickerProps,"onValueChange"> },
) => (
  <FastField name={name}>
    {({ field, form: { setFieldValue, setFieldTouched }, meta }) => (
      <FormControl
        isInvalid={meta.touched && !!meta.error}
        labelTx={`cycleForm.${_.camelCase(label)}` as TxKeyPath}
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        errorMessage={meta.error}
        style={FORM_DROPDOWN}
        {...formControlProps}
      >
        <DateTimePicker
          colorScheme={colorScheme}
          placeholderTx={`cycleForm.${_.camelCase(label)}Placeholder` as TxKeyPath}
          value={field.value}
          onValueChange={(value) => {
            setFieldTouched(field.name)
            setFieldValue(field.name, value)
          }}
          {...dateTimePickerProps}
        />
      </FormControl>
    )}
  </FastField>
)

const ModelBasic = (
  { name, value = "", idx, onChange, label, formControlProps, pickerProps, flex = false, colorScheme = Appearance.getColorScheme() } :
  { name: string, idx?: number, onChange?: (index, key, value) => void, colorScheme?: "light" | "dark", value?: string, label?: string, flex?: boolean, formControlProps?: FormControlProps, pickerProps?: PickerProps }
  ) => {
  return (
    <FastField name={name}>
      {({ field, form: { setFieldValue, setFieldTouched }, meta }) => (
        <FormControl
          colorScheme={colorScheme}
          isInvalid={meta.touched && !!meta.error}
          labelTx={`cycleForm.${_.camelCase(label)}` as TxKeyPath}
          labelColor={color.palette.gray800}
          style={flex ? FLEX_CONTENT : FORM_DROPDOWN}
          errorMessage={meta.error}
          {...formControlProps}
        >
          <View borderRadius="5px" backgroundColor={color.palette.white}>
            <Picker
              colorScheme={colorScheme}
              placeholderTx={`cycleForm.${_.camelCase(label)}Placeholder` as TxKeyPath}
              defaultValue={value}
              value={field.value}
              onValueChange={(index, value, item) => {
                setFieldTouched(field.name)
                setFieldValue(field.name, label !== 'tray'? item.value : index)
                onChange(idx, label, label !== 'tray'? item.value : index)
              }}
              isUseSearch={false}
              {...pickerProps}
            />
          </View>
        </FormControl>
      )}
    </FastField>
  )
}

const ModelField = (
  { name, value = "", index, label, formControlProps, modelPickerProps, colorScheme= Appearance.getColorScheme(), onChange }:
  {
    name: string, colorScheme?: "light" | "dark", value?: string, index?: number, label?: string,
    formControlProps?: FormControlProps, modelPickerProps?: Omit<ModelPickerProps, "onValueChange">, onChange?: (index, key, value) => void
  },
) => (
  <FastField name={name}>
    {({ field, form: { setFieldValue, setFieldTouched }, meta }) => (
      <FormControl
        isInvalid={meta.touched && !!meta.error}
        labelTx={`cycleForm.${_.camelCase(label)}` as TxKeyPath}
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        errorMessage={meta.error}
        style={FORM_DROPDOWN}
        {...formControlProps}
      >
        <ModelPicker
          colorScheme={colorScheme}
          placeholderTx={`cycleForm.${_.camelCase(label)}Placeholder` as TxKeyPath}
          defaultValue={value}
          value={field.value}
          onValueChange={(value) => {
            setFieldTouched(field.name)
            setFieldValue(field.name, value)
            onChange(index, label, value)
          }}
          isUseSearch
          {...modelPickerProps}
        />
      </FormControl>
    )}
  </FastField>
)

/**
 * Describe your component here
 */
export const CreateCycleForm = observer(function CreateCycleForm(props: CreateCycleFormProps) {
  const { initialValues = [], colorScheme = "light" || Appearance.getColorScheme(), onSubmit, onChange, onPress, next, innerRef, ...rest } = props

  const [ isHide, setHide] = useState<boolean>(true)

  yup.addMethod(yup.array, 'unique', function (message, mapper = a => a) {
    return this.test('unique', message, function (list) {
      return list.length === new Set(list.map(mapper)).size;
    });
  });

  const validationSchema = yup.object({
    cycles: yup.array().of(
        yup.object().shape({
        pond_id: yup.number().required().label(translate("validationInput")),
        total_seed: yup.number().required(translate("cantBeZero")).moreThan(0, translate("cantBeZero")),
        total_seed_type: yup.string().nullable().required().label(translate("validationInput")),
        created_at: yup.string().nullable().required().label(translate("validationInput")),
        deep: yup.number().required(translate("cantBeZero")).moreThan(0, translate("cantBeZero")),
        remark: yup.string().ascii().max(255).nullable().label(translate("cycleForm.remark")),
      })
    )
    .required()
  });

  const onClick = () => setHide(!isHide);

  return (
    <Formik
      initialValues={{cycles: initialValues}}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnMount
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
    {({ initialValues }) => (
      <>
        <FieldArray name="cycles"
          render={() => (
            <>
              {initialValues.cycles.map((item, index) => {
                return (
                  <View key={index} style={CONTAINER}>
                    <ModelField
                      name={`cycles[${index}].pond_id`}
                      index={index}
                      label="pondId"
                      formControlProps={{ isRequired: true }}
                      modelPickerProps={{
                        Model: Pond,
                        isDisabled: !!item.id,
                        clearable: true,
                        fetchData: (model, search) => (
                          model.fetch({
                            farm_id__eq: item.farmId,
                            name: search,
                            sort: 'name_alpha_numeric'
                          })
                        )
                      }}
                      onChange={onChange}
                      colorScheme={colorScheme}
                    />
                    <View style={FLEX_WRAPPER}>
                      <NumeralField name={`cycles[${index}].total_seed`} index={index} label="totalSeed" flex onChange={onChange} colorScheme={colorScheme} />
                      <ModelBasic
                        name={`cycles[${index}].total_seed_type`}
                        idx={index} label="totalSeedType"
                        flex onChange={onChange}
                        formControlProps={{ isRequired: true }}
                        pickerProps={{
                          dataKey: "value",
                          data: seedType,
                          titleTx: "cycleForm.totalSeedType",
                          renderSelectedItemText: (item) => item && translate(item.tx) as TxKeyPath,
                          renderSelectedItem(item) {
                            if (!item) return null;
                            return (<Text preset="body2" px="8px" text={translate(item.short)} colorScheme={colorScheme} />)
                          },
                        }}
                        colorScheme={colorScheme}
                      />
                    </View>
                    {/* <DateField name="spread" formControlProps={{ isRequired: true }} dateTimePickerProps={{ defaultValue: item.date }} colorScheme={colorScheme} /> */}
                    <DateField name={`cycles[${index}].created_at`} index={index} label="startedAt" formControlProps={{ isRequired: true }} dateTimePickerProps={{ defaultValue: item.date }} colorScheme={colorScheme} />
                    <View style={FLEX_WRAPPER}>
                      <NumeralField name={`cycles[${index}].age`} index={index} label="initialAge" flex onChange={onChange} colorScheme={colorScheme} />
                      <NumeralField name={`cycles[${index}].preparation_day`} index={index} label="preparationDay" flex onChange={onChange} colorScheme={colorScheme} />
                    </View>
                    <ModelField
                      // name={ isEdit ? `cycles[${index}].last_cycle.hatchery_id` : `cycles[${index}].hatchery` }
                      name={`cycles[${index}].hatchery_id`}
                      label="hatchery"
                      index={index}
                      onChange={onChange}
                      formControlProps={{ isRequired: false}}
                      modelPickerProps={{
                        Model: Hatchery,
                        clearable: false,
                        isUseSearch: true,
                        titleTx: 'cycleForm.hatcheryId',
                        fetchData: (model, search) => (
                          model.fetch({
                            name: search,
                            sort: 'name'
                          })
                        )
                      }}
                      colorScheme={colorScheme}
                    />
                    <ModelField
                      // name={ isEdit ? `cycles[${index}].last_cycle.species_id` : `cycles[${index}].species` }
                      name={`cycles[${index}].species_id`}
                      label="species"
                      index={index}
                      onChange={onChange}
                      modelPickerProps={{
                        Model: Species,
                        isUseSearch: false,
                        titleTx: 'cycleForm.speciesId',
                        pickerActionsheetProps: {
                          actionsheetStyle: ACTION_SHEET
                        }
                      }}
                      colorScheme={colorScheme}
                    />
                    <TouchableOpacity style={BTN_SEPARATOR} onPress={onClick}>
                      <Text preset="title3" tx="cycleForm.targetAndCycles" colorScheme={colorScheme} />
                      <JalaIcon name={isHide ? 'expand_more' : 'expand_less'} color={color.palette.gray800} size={28} />
                    </TouchableOpacity>
                    {!isHide && (
                      <View>
                        {/* <ModelBasic name={`cycles[${index}].tray`} idx={index} label="tray" value={tray} data={trayCount} onChange={onChange} formControlProps={{ isRequired: true}} /> */}
                        <ModelBasic name={`cycles[${index}].feeding_tray_count`} idx={index} label="feedingTrayCount" onChange={onChange} formControlProps={{ isRequired: true }} pickerProps={{ data: trayCount }} colorScheme={colorScheme} />
                        <NumeralField name={`cycles[${index}].limit_weight_per_area`} index={index} label="limitWeightPerArea" onChange={onChange} colorScheme={colorScheme} />
                        <NumeralField name={`cycles[${index}].target_cultivation_day`} index={index} label="targetCultivationDay" onChange={onChange} colorScheme={colorScheme} />
                        <View style={FLEX_WRAPPER}>
                          <NumeralField name={`cycles[${index}].target_size`} index={index} label="targetSize" flex onChange={onChange} colorScheme={colorScheme} />
                          <NumeralField name={`cycles[${index}].target_survival_rate`} index={index} label="targetSurvivalRate" flex onChange={onChange} colorScheme={colorScheme} />
                        </View>
                        <NumeralField name={`cycles[${index}].area`} index={index} label="area" onChange={onChange} colorScheme={colorScheme} />
                        <NumeralField name={`cycles[${index}].deep`} index={index} label="deep" onChange={onChange} colorScheme={colorScheme} />
                        <NumeralField name={`cycles[${index}].remark`} index={index} label="remark" onChange={onChange} colorScheme={colorScheme} />
                      </View>
                    )}
                  </View>
                )
              })}
            </>
          )}
        />
        {next && (
          <TouchableOpacity style={FORM_DROPDOWN} onPress={onPress}>
            <Text tx="cycleForm.nextPond" style={TEXT_NEXT} colorScheme={colorScheme} />
          </TouchableOpacity>
        )}
      </>
    )}
  </Formik>
  )
})

CreateCycleForm.displayName = 'CreateCycleForm'
